import type { GetCurrentLoginInformationsOutput } from "@repo/sync-utils/sync/api";

import {
  syncSessionKeys,
  syncSessionStorageVersion,
  syncSessionStorageVersionKey,
} from "src/hooks/syncSessionConfig";

import {
  clearAndResetCanonicalSyncSession,
  getCanonicalSyncSessionSnapshot,
  migratePersistedSyncSessionStorage,
  persistAndPublishCanonicalSyncSession,
  replaceCanonicalSyncSession,
  resetCanonicalSyncSession,
  subscribeCanonicalSyncSessionStorageEvents,
} from "./session-state";

type SyncSessionStorage = Pick<Storage, "getItem" | "removeItem" | "setItem">;

export function readPersistedSyncSession(
  storage = getBrowserSyncSessionStorage(),
): GetCurrentLoginInformationsOutput {
  if (!storage) return {};
  if (
    !migratePersistedSyncSessionStorage(
      storage,
      syncSessionKeys,
      syncSessionStorageVersionKey,
      syncSessionStorageVersion,
    )
  ) {
    return {};
  }
  return {
    apps: readJson(storage, syncSessionKeys.userApps),
    grantedPermissions: readJson(storage, syncSessionKeys.grantedPermissions),
    permissions: readJson(storage, syncSessionKeys.userPermission),
    user: readJson(storage, syncSessionKeys.user),
  };
}

export function persistAndPublishSyncSession(
  session: GetCurrentLoginInformationsOutput,
) {
  return persistAndPublishCanonicalSyncSession(session, persistSyncSession);
}

function persistSyncSession(session: GetCurrentLoginInformationsOutput) {
  const storage = getBrowserSyncSessionStorage();
  if (!storage) return;
  migratePersistedSyncSessionStorage(
    storage,
    syncSessionKeys,
    syncSessionStorageVersionKey,
    syncSessionStorageVersion,
  );
  storage.setItem(syncSessionKeys.user, JSON.stringify(session.user ?? null));
  storage.setItem(
    syncSessionKeys.userPermission,
    JSON.stringify(session.permissions ?? null),
  );
  storage.setItem(syncSessionKeys.userApps, JSON.stringify(session.apps ?? []));
  storage.setItem(
    syncSessionKeys.grantedPermissions,
    JSON.stringify(session.grantedPermissions ?? []),
  );
}

export function clearAndResetSyncSession() {
  const storage = getBrowserSyncSessionStorage();
  if (!storage) {
    resetCanonicalSyncSession();
    return;
  }

  clearAndResetCanonicalSyncSession(storage, syncSessionKeys);
}

export function retainSyncSessionRuntime() {
  const storage = getBrowserSyncSessionStorage();
  if (typeof window === "undefined" || !storage) return () => {};

  if (!getCanonicalSyncSessionSnapshot().hydrated) {
    replaceCanonicalSyncSession(readPersistedSyncSession(storage));
  }

  return subscribeCanonicalSyncSessionStorageEvents({
    eventSource: {
      addEventListener: (_type, listener) =>
        window.addEventListener(
          "storage",
          listener as (event: StorageEvent) => void,
        ),
      removeEventListener: (_type, listener) =>
        window.removeEventListener(
          "storage",
          listener as (event: StorageEvent) => void,
        ),
    },
    expectedStorage: storage,
    readPersisted: () => readPersistedSyncSession(storage),
    storageKeys: Object.values(syncSessionKeys),
  });
}

function readJson<T>(
  storage: Pick<Storage, "getItem">,
  key: string,
): T | undefined {
  const value = storage.getItem(key);
  if (!value) return undefined;
  try {
    return JSON.parse(value) as T;
  } catch {
    return undefined;
  }
}

function getBrowserSyncSessionStorage(): SyncSessionStorage | undefined {
  return typeof localStorage === "undefined" ? undefined : localStorage;
}
